import React, { useState, useEffect, useMemo } from 'react';
import { collectionRequestAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';
import './BillingPage.css';

interface BillingRecord {
  id: number;
  collectionId: number;
  date: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'cancelled';
  dueDate: string;
  paymentMethod?: string;
  paymentDate?: string;
  volume: number;
  baseRate: number;
  additionalFees?: number;
  discounts?: number;
  description: string;
  invoiceNumber: string;
  address: string;
  completedAt?: string;
  // 영수증 기준 필드 추가
  billingVolume?: number;
  ratePerLiter?: number;
  volumeCharge?: number;
  totalAmount?: number;
  paymentStatus?: string;
}

export function BillingPage() {
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: 'all',
    period: 'all',
    sort: 'date-desc'
  });

  // 실제 금액 계산 함수
  const calculateAmount = (volume: number): number => {
    const baseRate = 10000; // 기본 요금 10,000원
    const additionalRate = 500; // 10L 초과분당 500원
    const threshold = 10; // 10L까지는 기본 요금
    
    if (volume <= threshold) {
      return baseRate;
    } else {
      const additionalVolume = volume - threshold;
      const additionalFee = Math.ceil(additionalVolume) * additionalRate;
      return baseRate + additionalFee;
    }
  };

  // 실제 수거 데이터를 기반으로 결제 내역 생성
  const generateBillingFromCollection = (collectionData: any): BillingRecord => {
    // 영수증 기준 요금 계산
    const billingVolume = collectionData.billing_volume || 
                         collectionData.final_weight || 
                         collectionData.actual_volume || 
                         collectionData.estimated_volume || 0;
    
    const ratePerLiter = collectionData.rate_per_liter || 200;
    const volumeCharge = collectionData.volume_charge || Math.round(billingVolume * ratePerLiter);
    const totalAmount = collectionData.total_amount || volumeCharge;
    
    // 날짜 처리 개선
    const getDateString = (dateField: any): string => {
      if (!dateField) return new Date().toISOString();
      
      try {
        // 다양한 날짜 형식 처리
        if (typeof dateField === 'string') {
          // ISO 문자열인 경우
          if (dateField.includes('T') || dateField.includes('Z')) {
            return new Date(dateField).toISOString();
          }
          // 날짜만 있는 경우 (YYYY-MM-DD)
          if (dateField.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return new Date(dateField + 'T00:00:00').toISOString();
          }
          // 다른 형식의 경우
          return new Date(dateField).toISOString();
        }
        
        // Date 객체인 경우
        if (dateField instanceof Date) {
          return dateField.toISOString();
        }
        
        // 숫자 타임스탬프인 경우
        if (typeof dateField === 'number') {
          return new Date(dateField).toISOString();
        }
        
        return new Date().toISOString();
      } catch (error) {
        console.warn('Date parsing error:', error, 'for field:', dateField);
        return new Date().toISOString();
      }
    };
    
    // 결제 상태 결정 (DB에 저장된 값 우선 사용)
    let status: 'paid' | 'pending' | 'overdue' | 'cancelled';
    let paymentDate: string | undefined;
    let paymentMethod: string | undefined;
    
    if (collectionData.payment_status) {
      status = collectionData.payment_status as 'paid' | 'pending' | 'overdue' | 'cancelled';
    } else if (collectionData.status === 'cancelled') {
      status = 'cancelled';
    } else if (collectionData.status === 'completed') {
      status = 'paid';
      paymentDate = getDateString(collectionData.payment_date || collectionData.completed_at || collectionData.completedAt);
      paymentMethod = collectionData.payment_method || '신용카드';
    } else {
      const createdDate = new Date(getDateString(collectionData.created_at || collectionData.createdAt));
      const dueDate = new Date(createdDate);
      dueDate.setDate(dueDate.getDate() + 7); // 7일 후 마감
      
      if (new Date() > dueDate) {
        status = 'overdue';
      } else {
        status = 'pending';
      }
    }

    const createdDate = getDateString(collectionData.created_at || collectionData.createdAt);
    const completedDate = collectionData.completed_at || collectionData.completedAt;

    return {
      id: collectionData.id,
      collectionId: collectionData.id,
      date: createdDate,
      amount: totalAmount,
      status,
      dueDate: new Date(createdDate).toISOString(),
      paymentMethod,
      paymentDate,
      volume: billingVolume,
      baseRate: 0, // 영수증 기준으로 기본 요금 없음
      additionalFees: 0, // 영수증 기준으로 추가 요금 없음
      discounts: 0,
      description: `음식물 폐기물 수거 (${billingVolume}L)`,
      invoiceNumber: collectionData.invoice_number || `SSL-${String(collectionData.id).padStart(6, '0')}`,
      address: collectionData.collection_address || collectionData.address || '주소 정보 없음',
      completedAt: completedDate ? getDateString(completedDate) : undefined,
      // 영수증 기준 필드
      billingVolume: billingVolume,
      ratePerLiter: ratePerLiter,
      volumeCharge: volumeCharge,
      totalAmount: totalAmount,
      paymentStatus: collectionData.payment_status
    };
  };

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true);
        const response = await collectionRequestAPI.getRequests();
        
        console.log('API Response:', response);
        
        // API 응답 구조에 따라 데이터 추출
        let requests = [];
        if (Array.isArray(response)) {
          requests = response;
        } else if (response && response.items && Array.isArray(response.items)) {
          requests = response.items;
        } else if (response && response.data && Array.isArray(response.data)) {
          requests = response.data;
        } else if (response && response.requests && Array.isArray(response.requests)) {
          requests = response.requests;
        } else if (response && response.collection_requests && Array.isArray(response.collection_requests)) {
          requests = response.collection_requests;
        } else {
          console.warn('Unexpected API response structure:', response);
          // 빈 배열로 시작하여 에러를 방지
          requests = [];
        }
        
        console.log('Extracted requests:', requests);
        
        // 완료된 수거 요청만 필터링하여 결제 내역 생성
        const completedRequests = requests.filter((req: any) => 
          req.status === 'completed' || req.status === 'cancelled'
        );
        
        console.log('Completed requests:', completedRequests);
        
        const billingRecords = completedRequests.map((req: any) => {
          console.log('Processing request:', req);
          return generateBillingFromCollection(req);
        });
        setRecords(billingRecords);
        setFilteredRecords(billingRecords);
      } catch (err) {
        setError('결제 내역을 불러오는 중 오류가 발생했습니다.');
        console.error('Error fetching billing data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  // 필터링 및 정렬
  useEffect(() => {
    let filtered = [...records];

    // 상태별 필터링
    if (filters.status !== 'all') {
      filtered = filtered.filter(record => record.status === filters.status);
    }

    // 기간별 필터링
    if (filters.period !== 'all') {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

      switch (filters.period) {
        case '30days':
          filtered = filtered.filter(record => new Date(record.date) >= thirtyDaysAgo);
          break;
        case '90days':
          filtered = filtered.filter(record => new Date(record.date) >= ninetyDaysAgo);
          break;
      }
    }

    // 정렬
    switch (filters.sort) {
      case 'date-desc':
        filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case 'date-asc':
        filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case 'amount-desc':
        filtered.sort((a, b) => b.amount - a.amount);
        break;
      case 'amount-asc':
        filtered.sort((a, b) => a.amount - b.amount);
        break;
    }

    setFilteredRecords(filtered);
  }, [records, filters]);

  // 요약 통계 계산
  const summaryStats = useMemo(() => {
    const total = filteredRecords.reduce((sum, record) => sum + record.amount, 0);
    const paid = filteredRecords
      .filter(record => record.status === 'paid')
      .reduce((sum, record) => sum + record.amount, 0);
    const pending = filteredRecords
      .filter(record => record.status === 'pending')
      .reduce((sum, record) => sum + record.amount, 0);
    const overdue = filteredRecords
      .filter(record => record.status === 'overdue')
      .reduce((sum, record) => sum + record.amount, 0);

    return { total, paid, pending, overdue };
  }, [filteredRecords]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid': return '결제완료';
      case 'pending': return '결제대기';
      case 'overdue': return '연체';
      case 'cancelled': return '취소';
      default: return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'paid': return 'paid';
      case 'pending': return 'pending';
      case 'overdue': return 'overdue';
      case 'cancelled': return 'cancelled';
      default: return '';
    }
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const resetFilters = () => {
    setFilters({
      status: 'all',
      period: 'all',
      sort: 'date-desc'
    });
  };

  if (loading) {
    return (
      <div className="billing-page">
        <div className="billing-container">
          <div className="loading-container">
            <LoadingSpinner />
            <p>결제 내역을 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-page">
        <div className="billing-container">
          <div className="error-container">
            <div className="error-message">{error}</div>
            <button onClick={() => window.location.reload()}>
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="billing-container">
        <div className="billing-header">
          <h1>결제 내역</h1>
          <p>음식물 폐기물 수거 서비스 이용 요금과 결제 현황을 확인하세요.</p>
        </div>

        <div className="billing-controls">
          <div className="controls-row">
            <div className="filter-group">
              <label>결제 상태</label>
              <select
                className="filter-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="all">전체</option>
                <option value="paid">결제완료</option>
                <option value="pending">결제대기</option>
                <option value="overdue">연체</option>
                <option value="cancelled">취소</option>
              </select>
            </div>

            <div className="filter-group">
              <label>기간</label>
              <select
                className="filter-select"
                value={filters.period}
                onChange={(e) => handleFilterChange('period', e.target.value)}
              >
                <option value="all">전체</option>
                <option value="30days">최근 30일</option>
                <option value="90days">최근 90일</option>
              </select>
            </div>

            <div className="filter-group">
              <label>정렬</label>
              <select
                className="filter-select"
                value={filters.sort}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
              >
                <option value="date-desc">날짜 (최신순)</option>
                <option value="date-asc">날짜 (오래된순)</option>
                <option value="amount-desc">금액 (높은순)</option>
                <option value="amount-asc">금액 (낮은순)</option>
              </select>
            </div>

            <button className="reset-button" onClick={resetFilters}>
              필터 초기화
            </button>
          </div>
        </div>

        <div className="summary-grid">
          <div className="summary-card total">
            <div className="summary-icon">💰</div>
            <div className="summary-label">총 결제</div>
            <div className="summary-amount">{summaryStats.total.toLocaleString()}원</div>
          </div>
          <div className="summary-card completed">
            <div className="summary-icon">✅</div>
            <div className="summary-label">결제완료</div>
            <div className="summary-amount">{summaryStats.paid.toLocaleString()}원</div>
          </div>
          <div className="summary-card pending">
            <div className="summary-icon">⏳</div>
            <div className="summary-label">결제대기</div>
            <div className="summary-amount">{summaryStats.pending.toLocaleString()}원</div>
          </div>
          <div className="summary-card overdue">
            <div className="summary-icon">⚠️</div>
            <div className="summary-label">연체</div>
            <div className="summary-amount">{summaryStats.overdue.toLocaleString()}원</div>
          </div>
        </div>

        <div className="billing-records">
          {filteredRecords.length === 0 ? (
            <div className="no-records">
              <p>해당 조건의 결제 내역이 없습니다.</p>
            </div>
          ) : (
            filteredRecords.map((record) => (
              <div key={record.id} className="billing-record">
                <div className="record-header">
                  <div className="record-info">
                    <div className="record-id">{record.invoiceNumber}</div>
                    <div className="record-date">
                      {new Date(record.date).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                  <span className={`status-badge ${getStatusClass(record.status)}`}>
                    {getStatusText(record.status)}
                  </span>
                </div>

                <div className="record-content">
                  <div className="service-description">
                    <span className="service-icon">🌱</span>
                    {record.description}
                  </div>
                  
                  <div className="service-address">
                    <span>📍</span>
                    {record.address}
                  </div>

                  <div className="billing-breakdown">
                    <div className="breakdown-row">
                      <span className="breakdown-label">단가</span>
                      <span className="breakdown-amount">₩{record.ratePerLiter || 200}/L</span>
                    </div>
                    <div className="breakdown-row">
                      <span className="breakdown-label">과금 기준 용량</span>
                      <span className="breakdown-amount">{record.billingVolume || record.volume}L</span>
                    </div>
                    <div className="breakdown-row">
                      <span className="breakdown-label">총 금액</span>
                      <span className="breakdown-amount">₩{record.totalAmount?.toLocaleString() || record.amount.toLocaleString()}</span>
                    </div>
                  </div>

                  {record.paymentDate && (
                    <div className="payment-details">
                      결제일: {new Date(record.paymentDate).toLocaleDateString('ko-KR')} ({record.paymentMethod})
                    </div>
                  )}
                </div>

                <div className="record-actions">
                  <Link to={`/receipt/${record.id}`} className="receipt-button">
                    📄 영수증
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}